/**
 * Counter stats — animates once when scrolled into view.
 */
class CounterStats extends HTMLElement {
  connectedCallback() {
    if (this.dataset.counterStarted) return;
    this.dataset.counterStarted = 'true';

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      this.#setFinalValues();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        observer.disconnect();
        this.#run();
      },
      { threshold: 0.25, rootMargin: '0px 0px -5% 0px' }
    );
    observer.observe(this);
  }

  #setFinalValues() {
    this.querySelectorAll('[data-count-to]').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      const raw = el.dataset.countTo ?? '';
      const suffix = el.dataset.countSuffix || '';
      if (el.dataset.countStatic === 'true' || !CounterStats.#isNumericCounter(raw)) {
        el.textContent = `${raw}${suffix}`;
        return;
      }
      const to = CounterStats.#parse(raw);
      el.textContent = `${to}${suffix}`;
    });
  }

  #run() {
    const duration = 1400;
    const values = [...this.querySelectorAll('[data-count-to]')].filter(
      (el) => el instanceof HTMLElement
    );

    values.forEach((el) => {
      const raw = el.dataset.countTo ?? '';
      const suffix = el.dataset.countSuffix || '';
      if (el.dataset.countStatic === 'true' || !CounterStats.#isNumericCounter(raw)) {
        el.textContent = `${raw}${suffix}`;
        return;
      }

      const target = CounterStats.#parse(raw);
      const start = performance.now();

      const tick = (now) => {
        const progress = Math.min((now - start) / duration, 1);
        const eased = 1 - (1 - progress) ** 3;
        el.textContent = `${Math.round(target * eased)}${suffix}`;
        if (progress < 1) requestAnimationFrame(tick);
      };

      requestAnimationFrame(tick);
    });
  }

  /** @param {string | undefined} raw */
  static #parse(raw) {
    const n = parseFloat(String(raw || '').replace(/[^\d.]/g, ''));
    return Number.isFinite(n) ? n : 0;
  }

  /** @param {string | undefined} raw */
  static #isNumericCounter(raw) {
    const value = String(raw ?? '').trim();
    return value !== '' && /^\d+(\.\d+)?$/.test(value);
  }
}

if (!customElements.get('counter-stats')) {
  customElements.define('counter-stats', CounterStats);
}
